/**
 * Rankings & Achievements domain (Phase 2.3).
 * Projections from match history + tournament placements — no tournament engine writes.
 */

/** @typedef {'overall'|'season'|'state'|'organization'|'age'|'discipline'} RankingScope */

/** @typedef {'MS'|'WS'|'MD'|'WD'|'XD'|'OPEN'|'UNKNOWN'} DisciplineCode */

export const DISCIPLINES = Object.freeze({
  MS: "MS",
  WS: "WS",
  MD: "MD",
  WD: "WD",
  XD: "XD",
  OPEN: "OPEN",
  UNKNOWN: "UNKNOWN",
});

export const DISCIPLINE_LABELS = Object.freeze({
  MS: "Men's Singles",
  WS: "Women's Singles",
  MD: "Men's Doubles",
  WD: "Women's Doubles",
  XD: "Mixed Doubles",
  OPEN: "Open",
  UNKNOWN: "All disciplines",
});

export const AGE_CATEGORIES = Object.freeze({
  U13: "U13",
  U15: "U15",
  U17: "U17",
  U19: "U19",
  SENIOR: "Senior",
  MASTERS: "Masters",
  OPEN: "Open",
});

export const RANKING_ALGORITHMS = Object.freeze({
  POINTS_MVP: "points_mvp",
  // Future: ELO, BWF_STYLE
});

/**
 * Configurable MVP points. Swap algorithm by replacing this table
 * or providing a custom RankPointsTable to calculatePlayerRankings.
 */
export const DEFAULT_POINTS_TABLE = Object.freeze({
  algorithmId: RANKING_ALGORITHMS.POINTS_MVP,
  tournamentWinner: 100,
  runnerUp: 60,
  semiFinal: 40,
  quarterFinal: 20,
  roundOf16: 10,
  participation: 5,
  matchWin: 2,
  matchLoss: 0,
});

/**
 * Achievement definition catalog (awarded by projection).
 */
export const ACHIEVEMENT_DEFINITIONS = Object.freeze([
  {
    id: "first_tournament",
    title: "First Tournament",
    detail: "Completed your first competition",
    kind: "milestone",
  },
  {
    id: "first_match_win",
    title: "First Match Win",
    detail: "Recorded your first win",
    kind: "milestone",
  },
  {
    id: "champion",
    title: "Champion",
    detail: "Won a tournament",
    kind: "title",
  },
  {
    id: "wins_10",
    title: "10 Wins",
    detail: "Reach 10 career wins",
    kind: "volume",
  },
  {
    id: "wins_50",
    title: "50 Wins",
    detail: "Reach 50 career wins",
    kind: "volume",
  },
  {
    id: "wins_100",
    title: "100 Wins",
    detail: "Reach 100 career wins",
    kind: "volume",
  },
  {
    id: "winning_streak_5",
    title: "Winning Streak",
    detail: "Win 5 matches in a row",
    kind: "form",
  },
  {
    id: "perfect_tournament",
    title: "Perfect Tournament",
    detail: "Win every match in a competition (3+ matches)",
    kind: "title",
  },
  {
    id: "most_active",
    title: "Most Active Player",
    detail: "Play 20+ matches in a season",
    kind: "volume",
  },
  {
    id: "finalist",
    title: "Finalist",
    detail: "Reach a tournament final",
    kind: "title",
  },
]);

export const CURRENT_SEASON_KEY = "current";

/**
 * @param {number} [year]
 */
export function seasonKeyForYear(year = new Date().getFullYear()) {
  return String(year);
}

/**
 * @param {number | null | undefined} completedAtMs
 * @param {number} [nowMs]
 */
export function seasonKeyFromTimestamp(completedAtMs, nowMs = Date.now()) {
  if (completedAtMs == null) return seasonKeyForYear(new Date(nowMs).getFullYear());
  return seasonKeyForYear(new Date(completedAtMs).getFullYear());
}

/**
 * List seasons for UI: current + historical years with activity + current alias.
 * @param {number[]} [yearsWithActivity]
 * @param {number} [nowYear]
 */
export function listSeasonOptions(yearsWithActivity = [], nowYear = new Date().getFullYear()) {
  const years = new Set([nowYear, nowYear - 1, nowYear - 2, ...yearsWithActivity]);
  const sorted = [...years].filter((y) => y >= 2020 && y <= nowYear + 1).sort((a, b) => b - a);
  return [
    { key: CURRENT_SEASON_KEY, label: "Current Season", year: nowYear },
    ...sorted.map((y) => ({
      key: seasonKeyForYear(y),
      label: String(y),
      year: y,
      historical: y < nowYear,
    })),
  ];
}

/**
 * Resolve season filter to a concrete year (null = all-time).
 * @param {string} seasonKey
 * @param {number} [nowYear]
 */
export function resolveSeasonYear(seasonKey, nowYear = new Date().getFullYear()) {
  if (!seasonKey || seasonKey === "all") return null;
  if (seasonKey === CURRENT_SEASON_KEY) return nowYear;
  const y = Number(seasonKey);
  return Number.isFinite(y) ? y : nowYear;
}

/**
 * Infer age category from date of birth ISO / year.
 * @param {string | number | null | undefined} dateOfBirth
 * @param {number} [asOfMs]
 */
export function inferAgeCategory(dateOfBirth, asOfMs = Date.now()) {
  if (dateOfBirth == null || dateOfBirth === "") return AGE_CATEGORIES.OPEN;
  let birth;
  if (typeof dateOfBirth === "number") {
    birth = dateOfBirth < 3000 ? new Date(dateOfBirth, 0, 1) : new Date(dateOfBirth);
  } else {
    birth = new Date(dateOfBirth);
  }
  if (Number.isNaN(birth.getTime())) return AGE_CATEGORIES.OPEN;
  const age = Math.floor((asOfMs - birth.getTime()) / (365.25 * 24 * 3600 * 1000));
  if (age < 13) return AGE_CATEGORIES.U13;
  if (age < 15) return AGE_CATEGORIES.U15;
  if (age < 17) return AGE_CATEGORIES.U17;
  if (age < 19) return AGE_CATEGORIES.U19;
  if (age >= 35) return AGE_CATEGORIES.MASTERS;
  if (age >= 19) return AGE_CATEGORIES.SENIOR;
  return AGE_CATEGORIES.OPEN;
}

/**
 * Map history card / preferred event → discipline code (MVP heuristic).
 * @param {object} card
 * @param {{ genderHint?: 'M'|'W'|null }} [ctx]
 */
export function inferDisciplineFromCard(card, ctx = {}) {
  const t = String(card?.matchType || "").toLowerCase();
  const text = `${card?.resultHeadline || ""} ${card?.preferredEvent || ""}`.toLowerCase();
  if (t === "mixed" || /mixed/.test(text)) return DISCIPLINES.XD;
  if (t === "doubles") {
    if (/women|ladies|wd/.test(text) || ctx.genderHint === "W") return DISCIPLINES.WD;
    if (/men|md/.test(text) || ctx.genderHint === "M") return DISCIPLINES.MD;
    return DISCIPLINES.OPEN;
  }
  if (t === "singles") {
    if (/women|ladies|ws/.test(text) || ctx.genderHint === "W") return DISCIPLINES.WS;
    if (/men|ms/.test(text) || ctx.genderHint === "M") return DISCIPLINES.MS;
    return DISCIPLINES.OPEN;
  }
  return DISCIPLINES.UNKNOWN;
}

/**
 * Snapshot doc id helpers.
 */
export function playerRankingSnapshotId(playerId, seasonKey) {
  return `${playerId}_${seasonKey || CURRENT_SEASON_KEY}`;
}

export function organizationRankingSnapshotId(organizationId, seasonKey) {
  return `${organizationId}_${seasonKey || CURRENT_SEASON_KEY}`;
}
